import { Head } from "@inertiajs/react"
import { useState, useEffect, useRef } from "react"
import { Loader2 } from "lucide-react"
import { MarkdownEditor } from "@/components/editor/MarkdownEditor"
import { SaveNoteDialog } from "@/components/editor/SaveNoteDialog"
import { useAutoSave, type SaveStatus } from "@/hooks/useAutoSave"
import { useTranscriptionPolling } from "@/hooks/useTranscriptionPolling"
import { useBlockAutoReload } from "@/lib/standalone-recovery"
import type { FrontMatterField, FrontMatterValues } from "@/types/frontMatter"

interface RoutingRule {
  front_matter_field_id: number
  front_matter_option_id: number
  destination_folder: string
}

interface EditorProps {
  filePath?: string
  frontMatterFields: FrontMatterField[]
  textNotesDestination: string
  rootFolder: string
  fileNameTemplate: string
  routingRules: RoutingRule[]
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

function encodeDropboxPath(path: string): string {
  return path
    .split("/")
    .map((s) => encodeURIComponent(s))
    .join("/")
    .replace(/^\//, "")
}

export default function Editor({
  filePath,
  frontMatterFields,
  textNotesDestination,
  rootFolder,
  fileNameTemplate,
  routingRules,
}: EditorProps) {
  const [body, setBody] = useState("")
  const [frontMatter, setFrontMatter] = useState<FrontMatterValues>({})
  const [loading, setLoading] = useState(!!filePath)
  const [loadError, setLoadError] = useState(false)
  const [reloadCount, setReloadCount] = useState(0)
  const [clientModified, setClientModified] = useState<string | null>(null)
  const [serverModified, setServerModified] = useState<string | null>(null)

  const bodyRef = useRef(body)
  const frontMatterRef = useRef(frontMatter)

  // Read query params
  const url = typeof window !== "undefined" ? new URL(window.location.href) : null
  const transcriptionJobId = url?.searchParams.get("transcription_job_id") || null
  const destParam = url?.searchParams.get("dest") || null
  // Captured once on mount so reloads don't re-trigger new-note cursor placement.
  const [isNewNote] = useState(() => url?.searchParams.get("new") === "1")

  useEffect(() => {
    if (!isNewNote || typeof window === "undefined") return
    const u = new URL(window.location.href)
    if (u.searchParams.has("new")) {
      u.searchParams.delete("new")
      window.history.replaceState({}, "", u.toString())
    }
  }, [isNewNote])

  const { saveStatus, markUnsaved, save } = useAutoSave({ filePath: filePath || null })

  const { isTranscribing } = useTranscriptionPolling({
    transcriptionJobId,
    onComplete: () => {
      // Reload file content and remount editor when transcript is ready
      if (filePath) {
        loadFile(filePath).then(() => {
          setReloadCount((c) => c + 1)
        })
      }
    },
  })

  const effectiveSaveStatus: SaveStatus = isTranscribing ? "transcribing" : saveStatus

  // Hold off the standalone recovery reload while there are unsaved edits in
  // flight, so a backgrounded-then-resumed session never discards typed work.
  useBlockAutoReload(saveStatus === "unsaved" || saveStatus === "saving")

  useEffect(() => {
    if (filePath) {
      loadFile(filePath)
    }
  }, [filePath])

  async function loadFile(path: string) {
    setLoading(true)
    setLoadError(false)
    try {
      const res = await fetch(`/api/files/${encodeDropboxPath(path)}`, {
        headers: { "X-CSRF-Token": getCSRFToken() },
      })
      if (!res.ok) throw new Error("Failed to load file")
      const data = await res.json()
      setBody(data.body || "")
      setFrontMatter(data.front_matter || {})
      setClientModified(data.client_modified || null)
      setServerModified(data.server_modified || null)
      bodyRef.current = data.body || ""
      frontMatterRef.current = data.front_matter || {}
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }

  function handleContentChange(newFm: FrontMatterValues, newBody: string) {
    setBody(newBody)
    setFrontMatter(newFm)
    bodyRef.current = newBody
    frontMatterRef.current = newFm
    markUnsaved(newFm, newBody)
  }

  function handleManualSave() {
    save(frontMatterRef.current, bodyRef.current)
  }

  // New note: show save dialog first
  if (!filePath) {
    return (
      <>
        <Head title="New Note" />
        <SaveNoteDialog
          frontMatterFields={frontMatterFields}
          defaultDestination={destParam || textNotesDestination}
          rootFolder={rootFolder}
          fileNameTemplate={fileNameTemplate}
          routingRules={routingRules}
        />
      </>
    )
  }

  if (loading) {
    return (
      <>
        <Head title="Editor" />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    )
  }

  if (loadError) {
    return (
      <>
        <Head title="Editor" />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">Failed to load file</p>
            <button
              onClick={() => filePath && loadFile(filePath)}
              className="text-sm text-primary hover:text-primary/80"
            >
              Try again
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Head title={filePath.split("/").pop() || "Editor"} />
      <MarkdownEditor
        key={reloadCount}
        filePath={filePath}
        initialBody={body}
        initialFrontMatter={frontMatter}
        frontMatterFields={frontMatterFields}
        saveStatus={effectiveSaveStatus}
        onContentChange={handleContentChange}
        onSave={handleManualSave}
        clientModified={clientModified}
        serverModified={serverModified}
        autoFocus
        newNote={isNewNote}
        rootFolder={rootFolder}
      />
    </>
  )
}
